import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import WorkspaceNotice from '../components/WorkspaceNotice';
import { Check, Copy, RefreshCw } from 'lucide-react';
import { workspaceAccessMessage } from '../lib/workspaceAccess';

export default function Integration() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [webhook, setWebhook] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [copied, setCopied] = useState(null);

  const load = async () => {
    const { data } = await api.get('/organization');
    setData(data);
    setWebhook(data.organization.webhook_url || '');
  };

  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    setError(null);
    api.get('/organization')
      .then(({ data }) => {
        if (cancelled) return;
        setData(data);
        setWebhook(data.organization.webhook_url || '');
      })
      .catch((err) => {
        if (cancelled) return;
        setError(workspaceAccessMessage(err, 'Integration settings could not be loaded.'));
      });
    return () => { cancelled = true; };
  }, [isAdmin]);

  const copy = (key, value) => {
    navigator.clipboard.writeText(value);
    setCopied(key);
    setTimeout(() => setCopied(null), 1500);
  };

  const rotate = async () => {
    if (!window.confirm('Rotate API key? Existing key will stop working.')) return;
    try {
      await api.post('/organization/rotate-key');
      await load();
    } catch (err) {
      setError(workspaceAccessMessage(err, 'Could not rotate the API key.'));
    }
  };

  const saveWebhook = async () => {
    setSaving(true);
    try {
      await api.patch('/organization', { webhook_url: webhook || null });
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1500);
      await load();
    } catch (err) {
      setError(workspaceAccessMessage(err, 'Could not save the webhook URL.'));
    } finally {
      setSaving(false);
    }
  };

  const rotateSecret = async () => {
    if (data.organization.webhook_secret &&
        !window.confirm('Rotate webhook secret? Existing receivers will need to update.')) return;
    try {
      await api.post('/organization/rotate-webhook-secret');
      await load();
    } catch (err) {
      setError(workspaceAccessMessage(err, 'Could not rotate the webhook secret.'));
    }
  };

  if (!isAdmin) {
    return (
      <WorkspaceNotice
        testId="integration-denied"
        title="Integration unavailable"
        message="Only organization admins can manage API keys and webhooks."
        detail="Ask an organization admin to review your workspace role."
      />
    );
  }

  if (error) {
    return (
      <section data-testid="integration-error" className="border border-red-200 bg-red-50 p-6 text-red-800">
        <p className="text-xs font-mono uppercase tracking-widest text-red-500">Developers</p>
        <h1 className="mt-1 font-display text-2xl font-bold tracking-tight text-red-950">Integration unavailable</h1>
        <p className="mt-3 max-w-2xl text-sm leading-6 text-red-800">{error}</p>
      </section>
    );
  }

  if (!data) return <div className="text-sm text-zinc-500">Loading...</div>;

  return (
    <div data-testid="integration-page" className="space-y-6">
      <div>
        <p className="text-xs font-mono uppercase tracking-widest text-zinc-500">Developers</p>
        <h1 className="font-display text-3xl font-bold tracking-tight mt-1">Integration</h1>
      </div>

      <section className="border border-zinc-200 rounded-sm bg-white p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="font-display text-lg font-semibold">API key</h2>
            <p className="text-sm text-zinc-500 mt-1">Use this key to call idemGO from your backend (<code className="font-mono text-xs">/api/v1/*</code>).</p>
          </div>
          {isAdmin && (
            <button onClick={rotate} data-testid="rotate-key"
              className="inline-flex items-center gap-1.5 text-sm px-3 py-2 border border-zinc-200 rounded-sm hover:border-zinc-400">
              <RefreshCw className="w-3.5 h-3.5" strokeWidth={1.5}/> Rotate
            </button>
          )}
        </div>
        <div className="mt-4 flex items-center gap-2 border border-zinc-200 rounded-sm bg-zinc-50">
          <code className="flex-1 px-3 py-2 text-xs font-mono truncate" data-testid="api-key-display">{data.organization.api_key}</code>
          <button onClick={() => copy('api-key', data.organization.api_key)} className="px-3 py-2 border-l border-zinc-200 text-zinc-600 hover:text-black" data-testid="copy-api-key">
            {copied === 'api-key' ? <Check className="w-4 h-4"/> : <Copy className="w-4 h-4"/>}
          </button>
        </div>
        <p className="text-xs text-zinc-500 mt-2 font-mono">curl -H "Authorization: Bearer &lt;api_key&gt;" {process.env.REACT_APP_BACKEND_URL}/api/v1/verification-sessions</p>
      </section>

      <section className="border border-zinc-200 rounded-sm bg-white p-6">
        <h2 className="font-display text-lg font-semibold">Webhook URL</h2>
        <p className="text-sm text-zinc-500 mt-1">POSTed when a KYC verification completes.</p>
        <div className="mt-4">
          <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-1">Endpoint</label>
          <input data-testid="org-webhook" value={webhook} onChange={(e)=>setWebhook(e.target.value)} placeholder="https://example.com/idemgo"
            disabled={!isAdmin}
            className="w-full border border-zinc-200 rounded-sm px-3 py-2 text-sm font-mono focus:outline-none focus:border-black disabled:bg-zinc-50"/>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-3 mt-4">
            <button onClick={saveWebhook} disabled={saving} data-testid="save-webhook"
              className="text-sm px-3 py-2 bg-black text-white rounded-sm hover:bg-zinc-800 disabled:opacity-50">
              {saving ? 'Saving...' : 'Save webhook'}
            </button>
            {savedFlash && <span className="text-xs text-green-700 font-mono">SAVED</span>}
          </div>
        )}
      </section>

      <section className="border border-zinc-200 rounded-sm bg-white p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="font-display text-lg font-semibold">Webhook signing secret</h2>
            <p className="text-sm text-zinc-500 mt-1">
              Used to sign outgoing webhooks. Verify the <code className="font-mono text-xs">X-Idemgo-Signature</code> header to confirm authenticity.
            </p>
          </div>
          {isAdmin && (
            <button onClick={rotateSecret} data-testid="rotate-webhook-secret"
              className="inline-flex items-center gap-1.5 text-sm px-3 py-2 border border-zinc-200 rounded-sm hover:border-zinc-400">
              <RefreshCw className="w-3.5 h-3.5" strokeWidth={1.5}/> {data.organization.webhook_secret ? 'Rotate' : 'Generate'}
            </button>
          )}
        </div>
        <div className="mt-4 flex items-center gap-2 border border-zinc-200 rounded-sm bg-zinc-50">
          <code className="flex-1 px-3 py-2 text-xs font-mono truncate" data-testid="webhook-secret-display">
            {data.organization.webhook_secret || '- not set -'}
          </code>
          {data.organization.webhook_secret && (
            <button onClick={() => copy('webhook-secret', data.organization.webhook_secret)} className="px-3 py-2 border-l border-zinc-200 text-zinc-600 hover:text-black" data-testid="copy-webhook-secret">
              {copied === 'webhook-secret' ? <Check className="w-4 h-4"/> : <Copy className="w-4 h-4"/>}
            </button>
          )}
        </div>
      </section>
    </div>
  );
}
